import { Button, Card, Empty, Input, Select, Space, Tag, Timeline, Typography, message } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router";

import request from "@/api";
import { Operation_History_Enum } from "@/meta/logistics/operationHistory";
import OperationHistoryPanel from "@/views/components/OperationHistoryPanel";
import { useUserStore } from "@/views/stores";
import { getEnterpriseName } from "@/utils/customer";
import { areaLib } from "@/utils/areaLib";
import { fmtMoney } from "@/utils/money";
import { confirmDialog } from "@/utils/confirmDialog";
import type {
    DeliveryVo,
    Invoice,
    OrderFinishForm,
    OrderVo,
    OrderRemark,
    ReceivePayment,
    Refund,
    ServiceSpecialVo
} from "@zhihaoscm/entity";
import { BARGAIN_END_FLAG, COMMODITY_PRICE_TYPE, GROUP_PURCHASE_STATE, ORDER_TYPE } from "@zhihaoscm/meta";
import {
    BaseItem as ZhBaseItem,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    Grid as ZhGrid,
    Table as ZhTable,
    Tabs as ZhTabs,
    Upload as ZhUpload,
    type TableColumnProps,
    type UploadRequestOptions
} from "zhihao-ui-react";
import { TradeOrderCancelDialog } from "./TradeOrderCancelDialog";
import { TradeOrderFinishDialog } from "./TradeOrderFinishDialog";
import { TradeOrderSchedulePanel } from "./TradeOrderSchedulePanel";

export interface TradeOrderFinishConfirmVo {
    /** 是否存在完成后会自动取消的待发起或待确认提货。 */
    hasPendingCancelDelivery: boolean;
}

const orderStateLabels: Record<number, string> = {
    0: "--",
    1: "待付款",
    2: "待提货",
    3: "提货中",
    4: "已完成",
    5: "已取消",
    6: "待确认支付",
    7: "拼团中",
    8: "助力中"
};
const orderTypeLabels: Record<number, string> = { 1: "寄售商品", 2: "团购商品", 3: "捡漏商品" };
const orderAuditStateLabels: Record<number, string> = { 0: "待提交", 1: "待审核", 2: "已通过", 3: "未通过" };

/** 对齐旧 Vue 详情页：可取消状态包含待确认支付、提货中和拼团中。 */
export function shouldShowTradeOrderDetailCancel(
    order: { orderType?: number | string; state?: number | string },
    bargainEndFlag?: number | string
): boolean {
    const state = Number(order.state);
    const isUnfinishedBargain =
        Number(order.orderType) === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code &&
        Number(bargainEndFlag) === BARGAIN_END_FLAG.NO.code;
    return !isUnfinishedBargain && [1, 2, 3, 6, 7].includes(state);
}

const waybillStateLabels: Record<number, string> = {
    3: "待发航",
    4: "运输中",
    5: "待卸货",
    8: "已完成",
    10: "待付定金",
    13: "未完成"
};

const supervisionTimeline = [
    { value: 3, label: "待发航", tip: "船舶处于装货阶段，准备发航" },
    { value: 4, label: "运输中", tip: "船舶已发航，正在运输中" },
    { value: 5, label: "待卸货", tip: "船舶已到港，准备卸货" },
    { value: 8, label: "已完成", tip: "已卸货清仓完毕，船运单已完成" }
];

const matchingTimeline = [
    { value: 13, label: "未完成", tip: "已开始船运单履约流程" },
    { value: 8, label: "已完成", tip: "船运单已完成" }
];

function displayValue(value: unknown): string {
    return value === undefined || value === null || value === "" ? "-" : String(value);
}

function displayDate(value: unknown): string {
    if (!value) return "-";
    // 旧 Vue 详情页保留接口返回的完整日期时间，便于核对履约节点的实际时刻。
    return String(value).replace("T", " ");
}

function getAddress(delivery: DeliveryVo): string {
    const address = delivery.delivery?.addressInfo;
    if (!address) return "-";
    return [address.province, address.city, address.district, address.detailAddress].filter(Boolean).join("");
}

/** 订单收货地址沿用 Vue orderMessage 的省市区和详细地址拼接规则。 */
function getOrderAddress(order: OrderVo["order"]): string {
    const address = order.addressInfo;
    if (!address) return "--";
    return [
        address.contacts,
        address.cellphone,
        `${areaLib.getFullAreaName(address.province, address.city, address.district)}${address.detailAddress || ""}`
    ]
        .filter(Boolean)
        .join(" | ");
}

function getTimelineItems(delivery: DeliveryVo) {
    const waybill = delivery.transportOrderShip;
    if (!waybill) return [];
    const state = Number(waybill.state);
    const activities = Number(waybill.serviceType) === 2 ? matchingTimeline : supervisionTimeline;
    const currentIndex = activities.findIndex(item => item.value === state);
    // Vue 在待付定金等未进入履约节点的状态下不展示时间线，保持相同语义。
    if (currentIndex < 0) return [];
    const visibleItems = activities.slice(0, currentIndex + 1);
    return [...visibleItems].reverse();
}

const paymentTypeLabels: Record<number, string> = {
    1: "订单定金",
    2: "排期货款",
    3: "发航货款",
    4: "开仓货款",
    5: "订单货款"
};

const paymentStateLabels: Record<number, string> = {
    1: "待支付",
    2: "已支付 (线上)",
    3: "未支付",
    4: "待确认支付",
    5: "已支付 (线下)"
};

function TradeOrderPaymentPanel({ payments }: { payments: ReceivePayment[] }) {
    const paidTotal = payments
        .filter(payment => [2, 5].includes(Number(payment.state)))
        .reduce((total, payment) => total + Number(payment.amount || 0), 0);
    const columns: TableColumnProps<ReceivePayment>[] = [
        { key: "id", label: "收款编号", prop: "id", render: ({ row }) => row.id },
        {
            key: "deliveryId",
            label: "关联提货",
            prop: "deliveryId",
            render: ({ row }) => row.deliveryId || "--"
        },
        {
            key: "paymentType",
            label: "费用类型",
            prop: "paymentType",
            render: ({ row }) => paymentTypeLabels[Number(row.paymentType)] || "--"
        },
        { key: "remark", label: "费用说明", prop: "remark", render: ({ row }) => row.remark || "--" },
        {
            key: "unitPrice",
            label: "单价(元/吨)",
            prop: "unitPrice",
            render: ({ row }) => (row.unitPrice == null ? "--" : fmtMoney(row.unitPrice))
        },
        {
            key: "ton",
            label: "数量(吨)",
            prop: "ton",
            render: ({ row }) => (row.ton == null ? "--" : Number(row.ton).toLocaleString("en-US"))
        },
        {
            key: "amount",
            label: "支付金额(元)",
            prop: "amount",
            render: ({ row }) => (row.amount == null ? "--" : fmtMoney(row.amount))
        },
        {
            key: "paymentInstruction",
            label: "付款说明",
            prop: "paymentInstruction",
            render: ({ row }) => row.paymentInstruction || "--"
        },
        {
            key: "payTime",
            label: "支付时间",
            prop: "payTime",
            render: ({ row }) => displayDate(row.payTime)
        },
        {
            key: "confirmTime",
            label: "确认支付凭证时间",
            prop: "state",
            // 旧 Vue 仅在线下已支付状态展示最后一次确认凭证时间。
            render: ({ row }) => {
                const latestConfirm = row.confirmOperateInfo?.[row.confirmOperateInfo.length - 1];
                return Number(row.state) === 5 ? displayDate(latestConfirm?.confirmTime) : "--";
            }
        },
        {
            key: "state",
            label: "支付状态",
            prop: "state",
            render: ({ row }) => paymentStateLabels[Number(row.state)] || "--"
        },
        { key: "operation", label: "操作", prop: "operation", render: () => null }
    ];

    return (
        <section className="section-card mt-4" data-testid="trade-order-payment-panel" id="trade-order-detail-tab-2">
            <div className="trade-order-payment-header">
                <div className="section-title mb-0">收款记录</div>
                <span className="trade-order-payment-total">
                    收款金额总计: <strong>{paidTotal}</strong>
                </span>
                <div className="trade-order-payment-actions">
                    <Button type="primary">发起收款</Button>
                    <Button type="primary">新增退款</Button>
                </div>
            </div>
            <ZhTable<ReceivePayment>
                data={payments}
                columns={normalizeOperationColumns(columns, payments)}
                pagination={false}
                rowKey={payment => String(payment.id)}
            />
        </section>
    );
}

const refundStateLabels: Record<number, string> = {
    1: "待审核",
    2: "待退款",
    3: "已驳回",
    4: "已退款"
};

/** 对齐旧 Vue refundHistory，展示退款记录、状态和订单退款总额。 */
function TradeOrderRefundPanel({ refunds, totalAmount }: { refunds: Refund[]; totalAmount?: number }) {
    const columns: TableColumnProps<Refund>[] = [
        { key: "id", label: "退款", prop: "id", render: ({ row }) => row.id },
        { key: "refundType", label: "退款类型", prop: "refundType", render: ({ row }) => row.refundType || "--" },
        { key: "remark", label: "退款说明", prop: "remark", render: ({ row }) => row.remark || "--" },
        { key: "amount", label: "退款金额(元)", prop: "amount", render: ({ row }) => row.amount ?? "--" },
        { key: "refundTime", label: "退款时间", prop: "refundTime", render: ({ row }) => row.refundTime || "--" },
        {
            key: "state",
            label: "退款状态",
            prop: "state",
            render: ({ row }) => refundStateLabels[Number(row.state)] || "--"
        }
    ];

    return (
        <section className="section-card mt-4" data-testid="trade-order-refund-panel" id="trade-order-detail-tab-4">
            <div className="section-title mb-5">退款记录</div>
            <div className="mb-4">退款金额总计: {totalAmount ?? 0}</div>
            <ZhTable<Refund>
                data={refunds}
                columns={normalizeOperationColumns(columns, refunds)}
                pagination={false}
                rowKey={refund => String(refund.id)}
            />
        </section>
    );
}

/** 按旧 Vue tradeOrder/detail.vue 的状态规则生成订单详情导航项。 */
function getTradeOrderDetailTabs(detail: OrderVo, refunds: Refund[]): Array<{ key: string; label: string }> {
    const state = Number(detail.order.state);
    const tabs: Array<{ key: string; label: string }> = [{ key: "1", label: "订单信息" }];
    if (state !== 8) tabs.push({ key: "2", label: "收款记录" });
    if (refunds.length) tabs.push({ key: "4", label: "退款信息" });
    if ([2, 3, 4, 5].includes(state)) tabs.push({ key: "5", label: "发票信息" });
    if ([2, 3, 4, 5].includes(state) && detail.deliveryVos?.length) {
        tabs.push({ key: "3", label: "提货明细" });
    }
    return tabs;
}

/** 按旧 Vue isShowScheduleCalendar 规则决定订单排期区是否展示。 */
function shouldShowTradeOrderSchedule(detail: OrderVo): boolean {
    const order = detail.order;
    if (Number(order.orderOrigin) !== 1) return false;
    if (Number(order.orderType) === ORDER_TYPE.GROUP_BUYING_PRODUCTS.code) {
        return Number(detail.productGroupPurchase?.state) === GROUP_PURCHASE_STATE.SUCCESS.code;
    }
    if (Number(order.state) !== 5) return [2, 3, 4].includes(Number(order.state));
    // 取消订单沿用 Vue 的 beforeCancelState 判断，避免误用完成流程字段而丢失排期区。
    return Number(order.beforeCancelState) > 1;
}

/** 旧 Vue 的 deliveryInfo：排期日历前固定展示订单提货周期、吨数与提货地点。 */
function TradeOrderDeliveryInfoPanel({ detail }: { detail: OrderVo }) {
    const order = detail.order;
    const address = order.deliveryAddressInfo;
    const period =
        order.deliveryStartTime && order.deliveryEndTime
            ? `${displayDate(order.deliveryStartTime).slice(0, 10)} 至 ${displayDate(order.deliveryEndTime).slice(0, 10)}`
            : "--";

    return (
        <section className="section-card mt-4 trade-order-delivery-info-panel">
            <div className="section-title mb-5">提货信息</div>
            <ZhGrid cols={3} gap={[20, 20]}>
                <ZhBaseItem colon={false} label="提货周期" value={period} />
                <ZhBaseItem
                    colon={false}
                    label="可提货吨数"
                    value={order.ton ? `${Number(order.ton).toLocaleString("en-US")} 吨` : "--"}
                />
                <ZhBaseItem colon={false} label="提货地点">
                    <span>{address?.detailAddress || "--"}</span>
                    {address?.geo ? <i aria-hidden="true" className="iconfont icon-map lngLat" /> : null}
                </ZhBaseItem>
            </ZhGrid>
        </section>
    );
}

const deliveryStateLabels: Record<number, string> = {
    1: "待确认",
    2: "已确认",
    3: "已提货",
    4: "已取消",
    6: "已取消"
};

const deliveryTypeLabels: Record<number, string> = { 1: "排期产生", 2: "手动新增" };

/** 寄售订单沿用旧 Vue 的提货明细表；团购/捡漏继续使用履约详情面板。 */
function TradeOrderDeliveryListPanel({ deliveries }: { deliveries: DeliveryVo[] }) {
    const columns: TableColumnProps<DeliveryVo>[] = [
        {
            key: "id",
            label: "提货编号",
            prop: "delivery.id",
            minWidth: 180,
            render: ({ row }) => (
                <Link to={`/tradeManage/delivery/detail?id=${row.delivery?.id}`}>{row.delivery?.id || "--"}</Link>
            )
        },
        {
            key: "scheduleDate",
            label: "提货日期",
            prop: "delivery.scheduleDate",
            width: 190,
            render: ({ row }) => displayDate(row.delivery?.scheduleDate).slice(0, 10)
        },
        {
            key: "settlementAmount",
            label: "结算金额",
            prop: "delivery.settlementAmount",
            width: 170,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.settlementAmount)
        },
        {
            key: "estimatedTon",
            label: "提货吨位（吨）",
            prop: "delivery.estimatedTon",
            width: 170,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.estimatedTon)
        },
        {
            key: "settlementUnitAmount",
            label: "结算单价（元/吨）",
            prop: "delivery.settlementUnitAmount",
            width: 180,
            align: "right",
            render: ({ row }) => displayValue(row.delivery?.settlementUnitAmount)
        },
        {
            key: "state",
            label: "状态",
            prop: "delivery.state",
            width: 170,
            render: ({ row }) => deliveryStateLabels[Number(row.delivery?.state)] || "--"
        },
        {
            key: "scheduleId",
            label: "关联排期",
            prop: "delivery.scheduleId",
            minWidth: 180,
            render: ({ row }) => row.delivery?.scheduleId || "--"
        },
        {
            key: "type",
            label: "类型",
            prop: "delivery.type",
            width: 170,
            render: ({ row }) => deliveryTypeLabels[Number(row.delivery?.type)] || "--"
        },
        {
            key: "createdTime",
            label: "创建时间",
            prop: "delivery.createdTime",
            width: 190,
            render: ({ row }) => displayDate(row.delivery?.createdTime)
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            width: 240,
            render: () => <Button type="link">修改</Button>
        }
    ];

    return (
        <section className="section-card mt-4 trade-order-delivery-detail-panel">
            <div className="section-title mb-0">提货明细</div>
            <div className="trade-order-delivery-toolbar">
                <Button type="primary">新增提货</Button>
                <Input placeholder="请输入提货编号" />
                <Input placeholder="请输入提货日期" />
                <Select
                    placeholder="请选择提货状态"
                    options={Object.entries(deliveryStateLabels).map(([value, label]) => ({ value, label }))}
                />
                <Button type="primary">
                    <i aria-hidden="true" className="iconfont icon-Frame-15" />
                    搜索
                </Button>
                <Button className="trade-order-delivery-toolbar__refresh" aria-label="刷新提货明细">
                    <i aria-hidden="true" className="iconfont icon-Frame8" />
                </Button>
            </div>
            <ZhTable<DeliveryVo>
                data={deliveries}
                columns={normalizeOperationColumns(columns, deliveries)}
                pagination={false}
                rowKey={row => String(row.delivery?.id)}
            />
            <div className="trade-order-delivery-pagination">
                <span>共{deliveries.length}条数据</span>
                <span>
                    10条/页　‹　<span className="is-current">1</span>　›　前往&nbsp;1&nbsp;页
                </span>
            </div>
        </section>
    );
}

/** 对照 Vue deliveryMessage/logisticsTracking/proveMaterial，展示单条提货的履约闭环。 */
function DeliveryFulfillmentPanel({ deliveries }: { deliveries: DeliveryVo[] }) {
    if (!deliveries.length) {
        return (
            <Card className="mt-4" title="提货详情">
                <Empty description="暂无提货履约数据" />
            </Card>
        );
    }

    return (
        <Card className="mt-4" title="提货详情">
            {deliveries.map((delivery, index) => {
                const waybill = delivery.transportOrderShip;
                const details = delivery.transportOrderDetailsShip;
                const timelineItems = getTimelineItems(delivery);
                const waybillState = waybill ? waybillStateLabels[Number(waybill.state)] || "未知状态" : "未关联运单";
                const evidence = delivery.delivery?.evidenceMaterialInfo;
                const evidenceItems = [
                    ["吨位证明", evidence?.tonProveFileId],
                    ["砂石来源证明", evidence?.sandSourceProofFileId],
                    ["到装货港图片/视频", evidence?.loadingPortFileId],
                    ["清仓图片/视频", evidence?.clearanceSaleFileId]
                ];

                return (
                    <section key={delivery.delivery?.id || index} aria-label={`提货履约-${index + 1}`}>
                        {index > 0 ? <div className="my-4 border-t border-gray-200" /> : null}
                        <Typography.Title className="mb-3 mt-0" level={5}>
                            第 {index + 1} 条提货履约
                        </Typography.Title>
                        {/* 使用 zhihao-ui-react 网格承载履约信息，保持 Vue 字段顺序与显示口径。 */}
                        <ZhGrid cols={4} gap={[20, 20]}>
                            <ZhBaseItem label="履约状态">
                                <Tag color={waybill ? "blue" : "default"}>{waybillState}</Tag>
                            </ZhBaseItem>
                            <ZhBaseItem label="提货日期" value={displayDate(delivery.delivery?.scheduleDate)} />
                            <ZhBaseItem
                                label="提货吨数"
                                value={`${displayValue(delivery.delivery?.estimatedTon)} 吨`}
                            />
                            <ZhBaseItem
                                label="结算单价"
                                value={`${displayValue(delivery.delivery?.settlementUnitAmount)} 元/吨`}
                            />
                            <ZhBaseItem
                                label="关联运单"
                                value={waybill ? `${displayValue(waybill.id)}（${waybillState}）` : "-"}
                            />
                            <ZhBaseItem
                                label="船舶"
                                value={displayValue(
                                    delivery.delivery?.shipInfo?.cnname || delivery.delivery?.shipInfo?.name
                                )}
                            />
                            <ZhBaseItem label="收货人" value={displayValue(delivery.delivery?.addressInfo?.contacts)} />
                            <ZhBaseItem
                                label="收货联系方式"
                                value={displayValue(delivery.delivery?.addressInfo?.cellphone)}
                            />
                            <ZhBaseItem label="收货地址" span={4} value={getAddress(delivery)} />
                            <ZhBaseItem label="装货时间" value={displayDate(details?.deliveryInfo?.loadingDate)} />
                            <ZhBaseItem
                                label="集港货物吨位"
                                value={`${displayValue(details?.deliveryInfo?.portCargoTonnage)} 吨`}
                            />
                            <ZhBaseItem label="到港时间" value={displayDate(details?.captainArrivalTime)} />
                            <ZhBaseItem
                                label="卸货完成时间"
                                value={displayDate(details?.unloadingInfo?.unloadingCompletionTime)}
                            />
                        </ZhGrid>

                        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                            <Card size="small" title="物流追踪">
                                {waybill ? (
                                    <Timeline
                                        items={timelineItems.map(item => ({
                                            color: item.value === Number(waybill.state) ? "blue" : "gray",
                                            content: (
                                                <div>
                                                    <div>{item.label}</div>
                                                    <Typography.Text type="secondary">{item.tip}</Typography.Text>
                                                </div>
                                            )
                                        }))}
                                    />
                                ) : (
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂未关联运单数据" />
                                )}
                            </Card>
                            <Card size="small" title="佐证材料">
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                    {evidenceItems.map(([label, fileId]) => (
                                        <div key={label} className="rounded border border-dashed border-gray-300 p-2">
                                            <Typography.Text strong>{label}</Typography.Text>
                                            <div className="text-gray-500">
                                                {fileId ? `文件 ${fileId}` : "暂无材料"}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-2 rounded border border-dashed border-gray-300 p-2">
                                    <Typography.Text strong>地图/视频轨迹</Typography.Text>
                                    <div className="text-gray-500">
                                        {waybill ? "轨迹数据由运单服务提供，当前页面保留接口展示位" : "暂无轨迹数据"}
                                    </div>
                                </div>
                            </Card>
                        </div>
                    </section>
                );
            })}
        </Card>
    );
}

/** 对齐旧 Vue orderRemark：详情接口已返回备注时，展示备注人、时间、凭证和说明。 */
function TradeOrderRemarksPanel({ remarks }: { remarks: OrderRemark[] }) {
    if (!remarks.length) return null;

    return (
        <section className="section-card mt-4" data-testid="trade-order-remarks">
            <div className="section-title mb-5">订单备注</div>
            <div className="flex flex-col gap-4">
                {remarks.map(remark => (
                    <div key={remark.id} className="border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
                        <ZhGrid cols={8} gap={[20, 20]}>
                            <ZhBaseItem label="备注人">
                                <div>{displayValue(remark.handleName)}</div>
                                <div className="text-xs text-gray-500">{displayDate(remark.createdTime)}</div>
                            </ZhBaseItem>
                            <ZhBaseItem label="文件凭证" value={displayValue(remark.fileId)} />
                            <ZhBaseItem label="备注说明" span={6} value={displayValue(remark.remark)} />
                        </ZhGrid>
                    </div>
                ))}
            </div>
        </section>
    );
}

export interface TradeOrderDetailPageProps {
    addOrderRemark?: (params: { orderId: number | string; fileId: number; remark?: string }) => Promise<unknown>;
    assignOrder?: (params: { orderId: number | string; handlerId: string }) => Promise<unknown>;
    loadCommissioners?: () => Promise<ServiceSpecialVo[]>;
    loadDetail?: (id: number | string) => Promise<OrderVo>;
    loadRefunds?: (query: { page: number; size: number; orderId: string }) => Promise<TradeOrderRefundListResult>;
    cancelOrder?: (id: number | string) => Promise<unknown>;
    deleteOrder?: (id: number | string) => Promise<unknown>;
    finishOrder?: (id: number | string, form: OrderFinishForm) => Promise<unknown>;
    loadFinishConfirm?: (id: number | string) => Promise<TradeOrderFinishConfirmVo>;
    updatePickedUpTon?: (id: number | string, form: OrderFinishForm) => Promise<unknown>;
    loadInvoices?: (query: {
        page: number;
        size: number;
        orderId: string;
    }) => Promise<{ list?: Invoice[]; total?: number }>;
    permissions?: string[];
}

export interface TradeOrderRefundListResult {
    list?: Refund[];
    total?: number;
}

/** 复用 Vue 订单详情接口，展示详情页的基础订单信息。 */
export async function loadTradeOrderDetail(id: number | string): Promise<OrderVo> {
    const response = await request.service.get<OrderVo>(`/admin/order/vo/${id}`);
    return response.data;
}

/** 复用旧 Vue 订单详情的专员查询接口。 */
export async function loadTradeOrderDetailCommissioners(): Promise<ServiceSpecialVo[]> {
    const response = await request.service.get<ServiceSpecialVo[]>("/admin/order/find/specials");
    return response.data;
}

/** 复用旧 Vue 订单详情的专员指派接口。 */
export async function assignTradeOrderDetail(params: { orderId: number | string; handlerId: string }): Promise<void> {
    await request.service.put(`/admin/order/assign/${params.orderId}/${params.handlerId}`);
}

/** 复用 Vue 订单详情的订单备注接口，保持附件 ID 和备注字段结构。 */
export async function addTradeOrderDetailRemark(params: {
    orderId: number | string;
    fileId: number;
    remark?: string;
}): Promise<void> {
    await request.service.post("/admin/order-remark", params);
}

/** 复用 Vue 退款记录分页接口契约，仅读取详情页前五条退款记录。 */
export async function loadTradeOrderRefunds(query: {
    page: number;
    size: number;
    orderId: string;
}): Promise<TradeOrderRefundListResult> {
    const response = await request.service.get<TradeOrderRefundListResult>("/admin/refund/paging", {
        params: query
    });
    return response.data;
}

/** 复用旧 Vue 订单详情关联发票分页接口。 */
export async function loadTradeOrderInvoices(query: {
    page: number;
    size: number;
    orderId: string;
}): Promise<{ list?: Invoice[]; total?: number }> {
    const response = await request.service.get<{ list?: Invoice[]; total?: number }>("/admin/invoice/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 订单取消接口契约。 */
export async function cancelTradeOrderDetail(id: number | string) {
    const response = await request.service.put(`/admin/order/cancel/${id}`);
    return response.data;
}

/** 复用旧 Vue 订单删除接口，成功后由详情页返回列表。 */
export async function deleteTradeOrderDetail(id: number | string): Promise<void> {
    await request.service.delete(`/admin/order/${id}`);
}

/** 复用旧 Vue 订单详情完成接口，保持完成表单字段结构。 */
export async function finishTradeOrderDetail(id: number | string, form: OrderFinishForm): Promise<void> {
    await request.service.put(`/admin/order/finish/${id}`, form);
}

/** 复用旧 Vue 已完成订单修改提货吨数接口。 */
export async function updateTradeOrderPickedUpTon(id: number | string, form: OrderFinishForm): Promise<void> {
    await request.service.put(`/admin/order/update/ton/${id}`, form);
}

/** 复用旧 Vue 完成前确认接口，决定提货自动取消提示文案。 */
export async function loadTradeOrderFinishConfirm(id: number | string): Promise<TradeOrderFinishConfirmVo> {
    const response = await request.service.get<TradeOrderFinishConfirmVo>(`/admin/order/finish-confirm/${id}`);
    return response.data;
}

/** 将 Vue 交易订单详情返回的枚举字段统一为详情文案使用的数字。 */
function normalizeTradeOrderDetail(result: OrderVo): OrderVo {
    return {
        ...result,
        order: {
            ...result.order,
            orderType: Number(result.order.orderType),
            state: Number(result.order.state),
            beforeFinishState: Number(result.order.beforeFinishState),
            orderOrigin: Number(result.order.orderOrigin),
            auditState: Number(result.order.auditState),
            payType: Number(result.order.payType)
        },
        bargainEndFlag: Number(result.bargainEndFlag)
    };
}

/** 交易订单详情基础页，完整复用 Vue 详情数据、字段、履约区和操作流程。 */
export function TradeOrderDetailPage({
    addOrderRemark = addTradeOrderDetailRemark,
    assignOrder = assignTradeOrderDetail,
    loadCommissioners = loadTradeOrderDetailCommissioners,
    loadDetail = loadTradeOrderDetail,
    loadRefunds = loadTradeOrderRefunds,
    cancelOrder = cancelTradeOrderDetail,
    deleteOrder = deleteTradeOrderDetail,
    finishOrder = finishTradeOrderDetail,
    loadFinishConfirm = loadTradeOrderFinishConfirm,
    updatePickedUpTon = updateTradeOrderPickedUpTon,
    loadInvoices = loadTradeOrderInvoices,
    permissions
}: TradeOrderDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const orderId = new URLSearchParams(location.search).get("id") || "";
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    // 未传入 permissions 时保留现有页面测试和本地直达页面的默认可见行为；正式路由可传入权限快照。
    const currentPermissions = permissions ?? [
        "ROLE_TRADE_ORDER_MANAGE",
        "ROLE_TRADE_ORDER_DEAL",
        "ROLE_TRADE_ORDER_DELETE",
        "ROLE_TRADE_ORDER_CANCEL",
        ...storePermissions
    ];
    const [detail, setDetail] = useState<OrderVo | null>(null);
    const [refunds, setRefunds] = useState<Refund[]>([]);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [cancelError, setCancelError] = useState<string | null>(null);
    const [finishOpen, setFinishOpen] = useState(false);
    const [finishSubmitting, setFinishSubmitting] = useState(false);
    const [finishError, setFinishError] = useState<string | null>(null);
    const [editPickedUpTonOpen, setEditPickedUpTonOpen] = useState(false);
    const [editPickedUpTonSubmitting, setEditPickedUpTonSubmitting] = useState(false);
    const [editPickedUpTonError, setEditPickedUpTonError] = useState<string | null>(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [commissioners, setCommissioners] = useState<ServiceSpecialVo[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState("");
    const [assignError, setAssignError] = useState<string | null>(null);
    const [remarkOpen, setRemarkOpen] = useState(false);
    const [remarkFileId, setRemarkFileId] = useState("");
    const [remarkFileName, setRemarkFileName] = useState("");
    const [remarkContent, setRemarkContent] = useState("");
    const [remarkError, setRemarkError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState("1");
    const [operationLogRefreshToken, setOperationLogRefreshToken] = useState(0);

    /** 删除已完成/已取消订单并返回上一页，保持旧 Vue onDelete 行为。 */
    const handleDeleteOrder = async () => {
        if (!order) return;
        try {
            const confirmed = await confirmDialog("是否删除订单，删除后此订单不在展示", "提示");
            if (!confirmed) return;
            await deleteOrder(order.id);
            message.success("删除订单成功");
            navigate(-1);
        } catch {
            message.error("删除订单失败");
        }
    };

    /** 提交完成表单并重新读取详情，沿用 Vue complete-order-dialog 的刷新语义。 */
    const submitFinishOrder = async (form: OrderFinishForm) => {
        if (!order) return;
        setFinishSubmitting(true);
        setFinishError(null);
        try {
            await finishOrder(order.id, form);
            message.success("完成订单成功");
            setFinishOpen(false);
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setFinishError("完成订单失败");
        } finally {
            setFinishSubmitting(false);
        }
    };

    /** 按旧 Vue 状态规则打开完成弹窗或执行提货中的二次确认。 */
    const openFinishOrder = async () => {
        if (!order || ![2, 3].includes(order.state)) return;
        setFinishError(null);
        if (order.state === 2) {
            setFinishOpen(true);
            return;
        }
        try {
            const confirmInfo = await loadFinishConfirm(order.id);
            const confirmMessage = confirmInfo.hasPendingCancelDelivery
                ? "是否完成，完成后待发起和待确认的提货将自动取消且不能再提货。"
                : "是否完成，完成后不能再提货";
            const confirmed = await confirmDialog(confirmMessage, "提示", {
                confirmButtonText: "确认",
                cancelButtonText: "取消"
            });
            if (confirmed) await submitFinishOrder({});
        } catch {
            setFinishError("完成订单失败");
        }
    };

    /** 修改待提货直接完成订单的提货吨数和结算凭证，并刷新详情。 */
    const submitPickedUpTon = async (form: OrderFinishForm) => {
        if (!order) return;
        setEditPickedUpTonSubmitting(true);
        setEditPickedUpTonError(null);
        try {
            await updatePickedUpTon(order.id, form);
            message.success("修改提货吨数成功");
            setEditPickedUpTonOpen(false);
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setEditPickedUpTonError("修改提货吨数失败");
        } finally {
            setEditPickedUpTonSubmitting(false);
        }
    };

    /** 取消订单成功后重新读取详情，保持 Vue 页面刷新语义。 */
    const handleCancelOrder = () => {
        if (!order) return;
        setCancelError(null);
        setCancelOpen(true);
    };

    /** 提交详情页取消请求，成功后按 Vue 行为重新拉取订单详情。 */
    const submitCancelOrder = async () => {
        if (!order) return;
        setCancelSubmitting(true);
        setCancelError(null);
        try {
            await cancelOrder(order.id);
            message.success("取消订单成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
            setCancelOpen(false);
        } catch {
            setCancelError("交易订单取消失败");
        } finally {
            setCancelSubmitting(false);
        }
    };

    /** 打开指派弹窗并按旧 Vue 约定加载可选专员。 */
    const openAssignDialog = async () => {
        if (!order) return;
        setAssignOpen(true);
        setAssignError(null);
        setSelectedHandlerId(order.handlerId == null ? "" : String(order.handlerId));
        try {
            setCommissioners((await loadCommissioners()) || []);
        } catch {
            setCommissioners([]);
            setAssignError("订单专员查询失败");
        }
    };

    /** 提交指派并重新读取详情，保持旧 Vue 操作后的刷新语义。 */
    const submitOrderAssign = async () => {
        if (!order?.id || !selectedHandlerId) {
            setAssignError("请选择订单专员");
            return;
        }
        try {
            await assignOrder({ orderId: order.id, handlerId: selectedHandlerId });
            setAssignOpen(false);
            setAssignError(null);
            message.success("指派成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setAssignError("订单专员指派失败");
        }
    };

    /** 打开订单备注弹窗，沿用旧 Vue 的空表单初始化行为。 */
    const openOrderRemark = () => {
        setRemarkFileId("");
        setRemarkFileName("");
        setRemarkContent("");
        setRemarkError(null);
        setRemarkOpen(true);
    };

    /** 提交订单备注并刷新详情，附件 ID 为旧 Vue 接口必填字段。 */
    const submitOrderRemark = async () => {
        if (!order?.id || !remarkFileId.trim() || !Number.isFinite(Number(remarkFileId))) {
            setRemarkError("请上传附件");
            return;
        }
        try {
            const normalizedRemark = remarkContent.trim();
            await addOrderRemark({
                orderId: Number(order.id),
                fileId: Number(remarkFileId),
                ...(normalizedRemark ? { remark: normalizedRemark } : {})
            });
            setRemarkOpen(false);
            setRemarkError(null);
            message.success("备注成功");
            setDetail(normalizeTradeOrderDetail(await loadDetail(order.id)));
            setOperationLogRefreshToken(current => current + 1);
        } catch {
            setRemarkError("订单备注保存失败");
        }
    };

    /** 使用旧 Vue 文件服务上传备注附件，并将返回文件 ID 回填到表单。 */
    const uploadOrderRemarkFile = async (options: UploadRequestOptions) => {
        const file = options.file as File;
        const formData = new FormData();
        formData.append("file", file);
        try {
            const { fileService } = await import("@/api/services/fileService");
            const uploadedFile = await fileService.uploadFile(formData);
            if (!uploadedFile.id) throw new Error("上传响应缺少文件 ID");
            setRemarkFileId(String(uploadedFile.id));
            setRemarkFileName(file.name);
            setRemarkError(null);
            options.onSuccess(uploadedFile);
        } catch (error) {
            setRemarkError("订单备注附件上传失败");
            options.onError(error instanceof Error ? error : new Error("订单备注附件上传失败"));
        }
    };

    useEffect(() => {
        if (!orderId) {
            setErrorMessage("缺少订单编号");
            return;
        }
        let disposed = false;
        void loadDetail(orderId)
            .then(result => {
                if (disposed) return;
                const normalizedDetail = normalizeTradeOrderDetail(result);
                setDetail(normalizedDetail);
                setErrorMessage(null);
                if (normalizedDetail.order.state === 8) {
                    setRefunds([]);
                    return;
                }
                // 旧 Vue 在加载订单详情后同步读取退款分页，空列表时不生成退款页签。
                void loadRefunds({ page: 1, size: 5, orderId: String(normalizedDetail.order.id) })
                    .then(refundResult => {
                        if (!disposed) setRefunds(refundResult.list || []);
                    })
                    .catch(() => {
                        if (!disposed) setRefunds([]);
                    });
            })
            .catch(() => {
                if (!disposed) setErrorMessage("交易订单详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadDetail, orderId]);

    useEffect(() => {
        if (!detail || ![2, 3, 4, 5].includes(Number(detail.order.state))) return;
        void loadInvoices({ page: 1, size: 100, orderId: String(detail.order.id) })
            .then(result => setInvoices(result.list || []))
            .catch(() => setInvoices([]));
    }, [detail, loadInvoices]);

    const order = detail?.order;
    const canAssign = Boolean(order && order.state !== 5 && currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE"));
    const detailTabs = detail ? getTradeOrderDetailTabs(detail, refunds) : [];

    /** 复用旧 zhInsetTabs 的点击后平滑滚动行为，当前已迁移面板按稳定 ID 定位。 */
    const changeDetailTab = (key: string) => {
        setActiveTab(key);
        const target = document.getElementById(`trade-order-detail-tab-${key}`);
        if (target && typeof target.scrollIntoView === "function") {
            target.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    return (
        <section aria-label="trade-order-detail-page" className="flex h-full min-h-full flex-col">
            <>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {order ? (
                    <>
                        <div className="section-card">
                            <ZhDetailHeader
                                leftInfos={[
                                    { label: "订单编号", value: order.id || "-" },
                                    { label: "下单时间", value: displayDate(order.createdTime || order.orderTime) },
                                    { label: "订单类型", value: orderTypeLabels[order.orderType] || "-" }
                                ]}
                                rightInfos={[
                                    { label: "更新时间", value: displayDate(order.updatedTime) },
                                    { label: "订单专员", value: order.handlerName || "-" }
                                ]}
                                stateDesc={`${orderStateLabels[order.state] || "-"}${order.auditState ? `（${orderAuditStateLabels[order.auditState] || "-"}）` : ""}`}
                                title="订单详情"
                            />
                            <ZhTabs
                                activeKey={activeTab}
                                className="mt-4"
                                items={detailTabs}
                                onChange={changeDetailTab}
                            />
                        </div>
                        <div className="trade-order-detail-content">
                            <div className="trade-order-message mt-4" id="trade-order-detail-tab-1">
                                <div className="section-card order-info">
                                    <div className="section-title mb-5">订单信息</div>
                                    <ZhGrid cols={2} gap={[20, 12]}>
                                        <ZhBaseItem colon={false} label="采购用户">
                                            <span className="trade-order-detail__link">
                                                {`${getEnterpriseName(order.customerEnterprise)} | ${order.customerEnterprise?.mobile || "-"}`}
                                            </span>
                                        </ZhBaseItem>
                                        <ZhBaseItem
                                            colon={false}
                                            label="商品标题"
                                            value={order.productInfo?.title || "-"}
                                        />
                                        <ZhBaseItem colon={false} label="收货信息" value={getOrderAddress(order)} />
                                        <ZhBaseItem colon={false} label="交易单价">
                                            <span>{fmtMoney(order.unitPrice, "元/吨")}</span>
                                            {Number(order.productInfo?.priceType) ===
                                            COMMODITY_PRICE_TYPE.ESTIMATED.code ? (
                                                <span className="trade-order-estimated-price">
                                                    <i aria-hidden="true" className="iconfont icon-a-Frame38" />{" "}
                                                    预估价格
                                                </span>
                                            ) : null}
                                        </ZhBaseItem>
                                        <ZhBaseItem
                                            colon={false}
                                            label="采购吨数"
                                            value={order.ton ? order.ton.toLocaleString("en-US") : "--"}
                                        />
                                        <ZhBaseItem colon={false} label="订单专员" value={order.handlerName || "--"} />
                                        <ZhBaseItem
                                            colon={false}
                                            label="预估金额"
                                            value={order.estimatedAmount == null ? "--" : `${order.estimatedAmount}元`}
                                        />
                                        <ZhBaseItem
                                            colon={false}
                                            label="下单时间"
                                            value={displayDate(order.orderTime)}
                                        />
                                        <ZhBaseItem
                                            colon={false}
                                            label="锁货时间"
                                            value={displayDate(order.lockingTime)}
                                        />
                                        <ZhBaseItem colon={false} label="订单备注" value={order.remark || "--"} />
                                    </ZhGrid>
                                </div>
                                <div className="section-card order-product">
                                    <div className="section-title mb-5">订单商品</div>
                                    <div className="trade-order-product">
                                        <div className="trade-order-product__left">
                                            <div className="trade-order-product__image">
                                                {detail.mainFile?.path ? (
                                                    <img alt="商品图片" src={detail.mainFile.path} />
                                                ) : null}
                                            </div>
                                            <div className="trade-order-product__info">
                                                <div className="trade-order-product__name">
                                                    {order.productInfo?.title || "-"}
                                                </div>
                                                <ZhGrid cols={2} gap={[20, 12]}>
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="品类"
                                                        value={order.productInfo?.productTypeName || "-"}
                                                    />
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="规格"
                                                        value={order.productInfo?.productTypeSpec || "-"}
                                                    />
                                                    <ZhBaseItem
                                                        colon={false}
                                                        label="供应商"
                                                        value={getEnterpriseName(order.productInfo?.supplierEnterprise)}
                                                    />
                                                    <ZhBaseItem colon={false} label="商品编号">
                                                        <span className="trade-order-detail__link">
                                                            {order.productId || "-"}
                                                        </span>
                                                    </ZhBaseItem>
                                                </ZhGrid>
                                            </div>
                                        </div>
                                        {order.orderOrigin !== 1 ? null : (
                                            <span className="trade-order-product__snapshot">
                                                查看交易快照{" "}
                                                <i aria-hidden="true" className="iconfont icon-arrow-rifht" />
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {detail ? (
                                order && order.state !== 8 ? (
                                    <TradeOrderPaymentPanel payments={detail.receivePayments || []} />
                                ) : null
                            ) : null}
                            {detail && refunds.length ? (
                                <TradeOrderRefundPanel refunds={refunds} totalAmount={order?.refundAmount} />
                            ) : null}
                            {detail && order && [2, 3, 4, 5].includes(order.state) ? (
                                <section className="section-card mt-4" id="trade-order-detail-tab-5">
                                    <div className="section-title mb-5">发票信息</div>
                                    <ZhTable<Invoice>
                                        data={invoices}
                                        columns={[
                                            {
                                                key: "id",
                                                label: "开票编号",
                                                prop: "id",
                                                render: ({ row }) => (
                                                    <Link
                                                        to={`/tradeManage/invoiceDetail?id=${row.id}&time=${Date.now()}`}
                                                    >
                                                        {row.id}
                                                    </Link>
                                                )
                                            },
                                            {
                                                key: "amount",
                                                label: "开票金额(元)",
                                                prop: "amount",
                                                render: ({ row }) => row.amount
                                            },
                                            {
                                                key: "invoiceDate",
                                                label: "开票日期",
                                                prop: "invoiceDate",
                                                render: ({ row }) => displayDate(row.invoiceDate)
                                            },
                                            {
                                                key: "state",
                                                label: "状态",
                                                prop: "state",
                                                render: ({ row }) =>
                                                    ({ 1: "已开票", 2: "已作废" })[Number(row.state)] || "-"
                                            },
                                            {
                                                key: "operation",
                                                label: "操作",
                                                render: ({ row }) =>
                                                    row.invalidFileId || row.fileId ? (
                                                        <Button
                                                            aria-label={`预览发票${row.id}`}
                                                            onClick={() =>
                                                                window.open(
                                                                    `/dataService/admin/file/${row.invalidFileId || row.fileId}`,
                                                                    "_blank"
                                                                )
                                                            }
                                                        >
                                                            预览
                                                        </Button>
                                                    ) : null
                                            }
                                        ]}
                                        pagination={false}
                                        rowKey={invoice => String(invoice.id)}
                                    />
                                </section>
                            ) : null}
                            {detail && shouldShowTradeOrderSchedule(detail) ? (
                                <>
                                    <TradeOrderDeliveryInfoPanel detail={detail} />
                                    <TradeOrderSchedulePanel detail={detail} />
                                </>
                            ) : null}
                            {detail ? (
                                <div id="trade-order-detail-tab-3">
                                    {Number(detail.order.orderType) === 1 ? (
                                        <TradeOrderDeliveryListPanel deliveries={detail.deliveryVos || []} />
                                    ) : null}
                                    {/* 保留履约详情作为团购/捡漏路径的真实数据兜底；寄售路径由上方旧版明细表承载视觉。 */}
                                    <div
                                        className={
                                            Number(detail.order.orderType) === 1 ? "trade-order-delivery-fallback" : ""
                                        }
                                    >
                                        <DeliveryFulfillmentPanel deliveries={detail.deliveryVos || []} />
                                    </div>
                                </div>
                            ) : null}
                            {detail ? <TradeOrderRemarksPanel remarks={detail.remarks || []} /> : null}
                            {order ? (
                                <OperationHistoryPanel
                                    bizNo={order.id}
                                    module={
                                        order.orderOrigin === 2
                                            ? Operation_History_Enum.RECORD_ORDER
                                            : Operation_History_Enum.TRADE_ORDER
                                    }
                                    refreshToken={operationLogRefreshToken}
                                />
                            ) : null}
                        </div>
                    </>
                ) : null}
                {order ? (
                    <Space className="operation-fixed-btn" size={0}>
                        {order.orderOrigin !== 1 &&
                        !(order.auditState === 1 && !order.state) &&
                        currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button disabled={banButton}>
                                <Link aria-label="编辑订单" to={`/tradeManage/tradeOrder/form?id=${order.id}`}>
                                    编辑
                                </Link>
                            </Button>
                        ) : null}
                        {canAssign ? (
                            <Button
                                aria-label="指派订单专员"
                                disabled={banButton}
                                onClick={() => void openAssignDialog()}
                            >
                                {order.handlerId ? "变更指派" : "指派"}
                            </Button>
                        ) : null}
                        {[2, 3].includes(order.state) && currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE") ? (
                            <Button aria-label="完成订单" disabled={banButton} onClick={() => void openFinishOrder()}>
                                完成
                            </Button>
                        ) : null}
                        {currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button aria-label="订单备注" disabled={banButton} onClick={openOrderRemark}>
                                订单备注
                            </Button>
                        ) : null}
                        {currentPermissions.includes("ROLE_TRADE_ORDER_CANCEL") &&
                        shouldShowTradeOrderDetailCancel(order, detail.bargainEndFlag) ? (
                            <Button disabled={banButton} onClick={handleCancelOrder}>
                                取消订单
                            </Button>
                        ) : null}
                        {order.beforeFinishState === 2 &&
                        order.state === 4 &&
                        currentPermissions.includes("ROLE_TRADE_ORDER_MANAGE") ? (
                            <Button
                                aria-label="修改提货吨数"
                                onClick={() => {
                                    setEditPickedUpTonError(null);
                                    setEditPickedUpTonOpen(true);
                                }}
                            >
                                修改提货吨数
                            </Button>
                        ) : null}
                        {[4, 5].includes(order.state) && currentPermissions.includes("ROLE_TRADE_ORDER_DELETE") ? (
                            <Button aria-label="删除订单" disabled={banButton} onClick={() => void handleDeleteOrder()}>
                                删除
                            </Button>
                        ) : null}
                        {/* 审核失败的补录订单沿用 Vue 的订单处理权限编码，允许处理人员删除重录。 */}
                        {order.auditState && !order.state && currentPermissions.includes("ROLE_TRADE_ORDER_DEAL") ? (
                            <Button aria-label="删除订单" disabled={banButton} onClick={() => void handleDeleteOrder()}>
                                删除
                            </Button>
                        ) : null}
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                ) : null}
            </>
            <ZhDialog
                open={remarkOpen}
                title="订单备注"
                width={400}
                onCancel={() => setRemarkOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setRemarkOpen(false)}>取消</Button>
                        <Button type="primary" onClick={() => void submitOrderRemark()}>
                            确认备注
                        </Button>
                    </Space>
                }
            >
                {remarkError ? <Alert className="mb-4" message={remarkError} role="alert" type="error" /> : null}
                <ZhUpload
                    action="#"
                    aria-label="备注附件"
                    httpRequest={options => void uploadOrderRemarkFile(options)}
                    limit={1}
                    showFileList={false}
                >
                    <Button htmlType="button" type="link">
                        上传附件
                    </Button>
                </ZhUpload>
                {remarkFileName ? (
                    <Typography.Text className="mb-4 block">已选择：{remarkFileName}</Typography.Text>
                ) : null}
                <Input
                    aria-label="附件文件ID"
                    className="mb-4"
                    placeholder="请输入附件文件ID"
                    value={remarkFileId}
                    onChange={event => setRemarkFileId(event.target.value)}
                />
                <Input.TextArea
                    aria-label="备注内容"
                    maxLength={200}
                    placeholder="请输入备注内容"
                    value={remarkContent}
                    onChange={event => setRemarkContent(event.target.value)}
                />
            </ZhDialog>
            <ZhDialog
                open={assignOpen}
                modalClass="assign-dialog-modal"
                title="指派专员"
                width="842px"
                onCancel={() => setAssignOpen(false)}
                footer={
                    <Space>
                        <Button onClick={() => setAssignOpen(false)}>取消</Button>
                        <Button type="primary" onClick={() => void submitOrderAssign()}>
                            确认指派
                        </Button>
                    </Space>
                }
            >
                {assignError ? <Alert className="mb-4" message={assignError} role="alert" type="error" /> : null}
                <Select
                    allowClear
                    aria-label="订单专员"
                    options={commissioners.map(item => ({
                        label: `${item.user?.name || "-"}${item.orderCount == null ? "" : `（跟进 ${item.orderCount} 条）`}`,
                        value: String(item.user?.personId)
                    }))}
                    placeholder="请选择订单专员"
                    value={selectedHandlerId}
                    onChange={value => setSelectedHandlerId(value || "")}
                />
            </ZhDialog>
            <TradeOrderFinishDialog
                errorMessage={finishError}
                onCancel={() => {
                    if (!finishSubmitting) {
                        setFinishOpen(false);
                        setFinishError(null);
                    }
                }}
                onConfirm={form => void submitFinishOrder(form)}
                order={finishOpen ? detail : null}
                submitting={finishSubmitting}
            />
            <TradeOrderFinishDialog
                errorMessage={editPickedUpTonError}
                initialForm={{ pickedUpTon: order?.pickedUpTon, settlementIds: order?.settlementIds }}
                mode="edit"
                onCancel={() => {
                    if (!editPickedUpTonSubmitting) {
                        setEditPickedUpTonOpen(false);
                        setEditPickedUpTonError(null);
                    }
                }}
                onConfirm={form => void submitPickedUpTon(form)}
                order={editPickedUpTonOpen ? detail : null}
                submitting={editPickedUpTonSubmitting}
            />
            <TradeOrderCancelDialog
                errorMessage={cancelError}
                onCancel={() => {
                    if (!cancelSubmitting) {
                        setCancelOpen(false);
                        setCancelError(null);
                    }
                }}
                onConfirm={() => void submitCancelOrder()}
                order={cancelOpen && detail ? detail : null}
                submitting={cancelSubmitting}
            />
        </section>
    );
}

export default TradeOrderDetailPage;
